import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { KNOWLEDGE_BASE_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

const keyFeatures = [
    {
        title: "Multi-Source Ingestion",
        description: "Crawl websites, sitemaps, FAQ pages, blogs, PDFs, DOCX, Notion, and Google Docs — all into one searchable memory.",
    },
    {
        title: "Automatic Chunking & Embeddings",
        description: "Smart chunking is tuned for high recall with low token cost. No prompt engineering or embedding-config required.",
    },
    {
        title: "Scheduled Recrawls",
        description: "Set a cadence — daily, weekly, or on-demand. The agent keeps your knowledge fresh as your content evolves.",
    },
    {
        title: "Grounded Answers with Citations",
        description: "Every reply links back to the source page or document. No hallucinations, no surprises — just verifiable answers.",
    },
    {
        title: "Selective Crawling",
        description: "Include / exclude rules let you hand-pick exactly which pages or directories the agent should learn from.",
    },
    {
        title: "Delta Updates",
        description: "We detect what's changed since the last crawl, so re-indexing is fast, incremental, and cheap.",
    },
    {
        title: "Hybrid Search",
        description: "Vector similarity plus keyword + filter ranking — surfaces exact matches and semantic neighbors in the same query.",
    },
    {
        title: "Multi-Workspace",
        description: "Separate knowledge bases per brand, product, or region. Each chat or call agent picks the one it needs.",
    },
    {
        title: "Privacy-First",
        description: "Region-locked storage, content-level access controls, and full audit logs. SOC 2-aligned, GDPR / DPDP friendly.",
    },
];

const ingestionSources = [
    { name: "Website crawl", icon: "🌐" },
    { name: "Sitemaps", icon: "🗺️" },
    { name: "PDF / DOCX", icon: "📄" },
    { name: "FAQ sheets", icon: "❓" },
    { name: "Help-desk articles", icon: "🧩" },
    { name: "Notion / Google Docs", icon: "📚" },
];

const primaryKeywords = [
    "AI knowledge base",
    "Vector search for chatbot",
    "Document chatbot",
    "AI search on your docs",
    "RAG chatbot",
    "Retrieval-augmented generation",
    "Semantic search SaaS",
    "Custom GPT for your data",
];

export default function KnowledgeBaseMarketingPage() {
    usePageSeo({
        title: "AI Knowledge Base — Crawl, Embed & Ground Answers in Your Content | FloGPT",
        description:
            "Connect websites, PDFs, FAQs, and docs. FloGPT crawls, chunks, embeds, and keeps everything fresh — so chat and voice agents answer with grounded citations from your own content.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-knowledge-base",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Knowledge Base",
                description:
                    "Knowledge ingestion engine that crawls websites, PDFs, and docs into a semantic vector index — so chat and voice agents reply with grounded, cited answers from your own content.",
                path: "/ai-knowledge-base",
                serviceType: "AI Knowledge Base / Retrieval-Augmented Generation",
                features: [
                    "Multi-source ingestion: websites, sitemaps, PDFs, DOCX, Notion, Google Docs",
                    "Automatic chunking and embeddings",
                    "Scheduled re-crawls with delta updates",
                    "Hybrid semantic + keyword search",
                    "Inline citations on every answer",
                    "Multi-workspace scoping",
                    "Region-locked storage and audit logs",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-pink-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Knowledge Base · Crawl · Embed · Ground
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    Your Content, Instantly{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Query-Ready
                                    </motion.span>
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    Connect websites, PDFs, FAQs, and docs. FloGPT crawls, chunks, embeds, and keeps everything fresh with scheduled recrawls. Chat and voice agents reply with grounded citations from your own content.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Start Building Free
                                    </Link>
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a Demo
                                    </a>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· Hybrid vector + keyword search</span>
                                    <span>· Citations on every answer</span>
                                    <span>· Daily recrawls</span>
                                </div>
                            </motion.div>

                            {/* Animated KB preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-pink-500/25 to-orange-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                                                KB
                                            </div>
                                            <div>
                                                <div className="text-white font-semibold">docs.example.com</div>
                                                <div className="text-xs text-slate-400">2,847 chunks · last sync 03:14 UTC</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-emerald-300">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                            Live crawl
                                        </div>
                                    </div>
                                    <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                        {[
                                            "POST /knowledge/crawl",
                                            "payload:",
                                            "  url: 'https://docs.example.com'",
                                            "  include: ['/*.md', '/guides/*']",
                                            "  schedule: '0 3 * * *'  # daily at 3am",
                                            "  embedding: 'text-embedding-3-large'",
                                        ].map((line, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                <span className="whitespace-pre-wrap">{line}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm">
                                        <div className="text-emerald-300 font-semibold mb-2">Answer · How do I integrate?</div>
                                        <ul className="space-y-1 text-slate-200 text-[13px]">
                                            <li>· Use OAuth 2.0 with PKCE</li>
                                            <li>· Create a Client ID in dashboard → Apps</li>
                                            <li>· Add your redirect URL</li>
                                        </ul>
                                        <div className="mt-3 text-[11px] text-orange-300">[source] docs.example.com/guide#oauth</div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Most chatbots make stuff up. Yours won't.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Generic LLMs hallucinate the moment a question goes off-script. RAG fixes that — but only if the retrieval pipeline is built right.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                FloGPT pairs hybrid (vector + keyword) retrieval with strict citation enforcement, so every answer is anchored in a passage we can show the user.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Stop maintaining a bot's "knowledge"
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Your docs already exist. Your help center already answers most questions. Your team already wrote a hundred FAQs.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's knowledge base:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Crawls everything — websites, sitemaps, PDFs, DOCX, FAQs",
                                    "Refreshes on a schedule so the agent never goes stale",
                                    "Grounds every answer with linked citations",
                                    "Indexes incrementally so re-crawls are cheap",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">Update your docs. The agent updates itself.</p>
                        </article>
                    </motion.div>
                </section>

                {/* INGESTION SOURCES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Ingest from anywhere</h2>
                        <p className="mt-4 text-slate-300">
                            Whatever format your knowledge already lives in — we'll read it.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                        {ingestionSources.map((s) => (
                            <motion.div
                                key={s.name}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                viewport={{ once: true }}
                                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-center shadow"
                            >
                                <div className="text-3xl">{s.icon}</div>
                                <div className="mt-2 text-sm font-semibold text-slate-200">{s.name}</div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the FloGPT knowledge base does</h2>
                        <p className="mt-4 text-slate-300">
                            From crawl to citation, every step is tuned for accuracy and freshness.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 border border-orange-400/30">
                                        <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h2 className="text-lg font-semibold text-white">Pair the knowledge base with</h2>
                        <p className="mt-2 text-slate-400 text-sm">A KB is only as useful as the agents that read from it.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                            <Link to="/ai-chat-widget" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Chat Widget →</div>
                                <p className="mt-1 text-sm text-slate-400">Embed an AI chat widget that answers from your KB on any website.</p>
                            </Link>
                            <Link to="/ai-voice-agents" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Voice Agents →</div>
                                <p className="mt-1 text-sm text-slate-400">Run inbound and outbound voice agents that look things up mid-call.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                <FAQSection
                    id="knowledge-base"
                    eyebrow="Knowledge Base · FAQ"
                    heading="Common questions about the AI knowledge base"
                    items={KNOWLEDGE_BASE_FAQS}
                />

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">Turn your docs into a 24/7 expert</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll connect your knowledge base and demo a working agent on your own content.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Knowledge Base Demo"
                            subtitle="Send us a URL — we'll crawl it and run a live agent on your content"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>
            </main>
            <Footer />

            <JsonLd id="service-knowledge-base" data={serviceSchema} />
        </div>
    );
}
